/**
 * mediaStore.js — on-device storage for location photos and short videos,
 * using the Origin Private File System (OPFS).
 *
 * Files are written under an OPFS `media/` directory with a generated
 * filename; the app keeps only that filename in the database (see
 * location_media). This keeps large binaries out of the SQLite rows while
 * still being fully offline and private to the origin.
 *
 * A future media sync can read each file back via getMediaBlob() and upload
 * it, then mark the location_media row synced.
 *
 * @module media/mediaStore
 */

const MEDIA_DIR = 'media';
const MAX_VIDEO_SECONDS = 15;
const MAX_PHOTO_BYTES = 12 * 1024 * 1024;  // 12 MB safety cap (before compression)
const MAX_VIDEO_BYTES = 60 * 1024 * 1024;  // 60 MB safety cap

// Photo compression (Canvas-based, no dependencies). Downscaling to a sane max
// dimension + re-encoding cuts a typical 3–12 MB phone photo to ~150–400 KB
// with no visible loss at display size. WebP is preferred; JPEG is the
// fallback for runtimes whose canvas can't encode WebP (older iOS Safari).
const PHOTO_MAX_EDGE = 1600;     // px, long edge
const PHOTO_QUALITY  = 0.8;      // 0–1

/** @returns {Promise<FileSystemDirectoryHandle>} the OPFS media directory */
async function _mediaDir() {
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(MEDIA_DIR, { create: true });
}

/** True if OPFS is usable in this runtime. */
export function isSupported() {
  return typeof navigator !== 'undefined'
    && navigator.storage
    && typeof navigator.storage.getDirectory === 'function';
}

/**
 * Classify + validate a picked File. For videos, resolves the duration and
 * rejects anything longer than 15 s.
 *
 * @param {File} file
 * @returns {Promise<{ ok:boolean, kind?:'photo'|'video', durationMs?:number, error?:string }>}
 */
export async function inspectMedia(file) {
  if (!file) return { ok: false, error: 'No file' };
  const type = file.type || '';

  if (type.startsWith('image/')) {
    if (file.size > MAX_PHOTO_BYTES) return { ok: false, error: 'Photo is too large (max 12 MB)' };
    return { ok: true, kind: 'photo' };
  }

  if (type.startsWith('video/')) {
    if (file.size > MAX_VIDEO_BYTES) return { ok: false, error: 'Video is too large' };
    let durationMs;
    try {
      durationMs = await _videoDurationMs(file);
    } catch (_) {
      return { ok: false, error: 'Could not read the video' };
    }
    if (durationMs > MAX_VIDEO_SECONDS * 1000 + 400 /* small tolerance */) {
      return { ok: false, error: `Video must be ${MAX_VIDEO_SECONDS} seconds or less` };
    }
    return { ok: true, kind: 'video', durationMs };
  }

  return { ok: false, error: 'Only photos and short videos are allowed' };
}

/**
 * Persist a File into OPFS. Returns the stored reference + metadata.
 *
 * @param {File} file
 * @param {{ kind?:string, durationMs?:number }} [meta] result of inspectMedia
 * @returns {Promise<{ filename:string, kind:string, mime:string, size:number, durationMs:number|null }>}
 */
export async function saveMediaFile(file, meta = {}) {
  if (!isSupported()) throw new Error('OPFS not available');
  const kind = meta.kind || (file.type.startsWith('video/') ? 'video' : 'photo');

  // Photos are downscaled + re-encoded before storage. Video is stored as-is
  // (client-side transcoding is too heavy for low-end devices; compress
  // server-side on upload instead).
  let blob = file;
  let mime = file.type || null;
  if (kind === 'photo') {
    try {
      const compressed = await _compressPhoto(file);
      if (compressed) { blob = compressed.blob; mime = compressed.mime; }
    } catch (err) {
      console.warn('[media] photo compression failed, storing original:', err?.message || err);
    }
  }

  const ext = _extForBlob(blob, file);
  const filename = `${_uuid()}.${ext}`;

  const dir = await _mediaDir();
  const handle = await dir.getFileHandle(filename, { create: true });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();

  return {
    filename,
    kind,
    mime,
    size: blob.size ?? null,
    durationMs: meta.durationMs ?? null,
  };
}

/**
 * Downscale + re-encode a photo via Canvas. Returns the smaller of the
 * compressed result and the original (never inflates a small file). Prefers
 * WebP, falls back to JPEG.
 * @param {File|Blob} file
 * @returns {Promise<{ blob:Blob, mime:string }|null>}
 */
async function _compressPhoto(file) {
  const bitmap = await _decodeImage(file);
  if (!bitmap) return null;

  const { width, height } = bitmap;
  const scale = Math.min(1, PHOTO_MAX_EDGE / Math.max(width, height));
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);
  if (bitmap.close) bitmap.close();

  // Try WebP first, then JPEG.
  let out = await _canvasToBlob(canvas, 'image/webp', PHOTO_QUALITY);
  if (!out || out.type !== 'image/webp') {
    out = await _canvasToBlob(canvas, 'image/jpeg', PHOTO_QUALITY);
  }
  if (!out) return null;

  // Never store something larger than the original.
  if (file.size && out.size >= file.size) {
    return { blob: file, mime: file.type || 'image/jpeg' };
  }
  return { blob: out, mime: out.type };
}

function _decodeImage(file) {
  // Prefer createImageBitmap (fast, off-thread); fall back to an <img>.
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file).catch(() => _decodeViaImg(file));
  }
  return _decodeViaImg(file);
}

function _decodeViaImg(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

function _canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    if (canvas.toBlob) canvas.toBlob((b) => resolve(b), type, quality);
    else resolve(null);
  });
}

/**
 * Read a stored media file back as a Blob (for preview or upload).
 * @param {string} filename
 * @returns {Promise<Blob>}
 */
export async function getMediaBlob(filename) {
  const dir = await _mediaDir();
  const handle = await dir.getFileHandle(filename);
  return handle.getFile();
}

/**
 * Object URL for a stored file (remember to URL.revokeObjectURL when done).
 * @param {string} filename
 * @returns {Promise<string>}
 */
export async function getMediaObjectURL(filename) {
  const blob = await getMediaBlob(filename);
  return URL.createObjectURL(blob);
}

/**
 * Delete a stored media file (best effort).
 * @param {string} filename
 */
export async function deleteMediaFile(filename) {
  try {
    const dir = await _mediaDir();
    await dir.removeEntry(filename);
  } catch (_) { /* already gone */ }
}

// ---------- helpers ----------

function _videoDurationMs(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      const d = v.duration;
      if (d === Infinity || Number.isNaN(d)) { reject(new Error('unknown duration')); return; }
      resolve(Math.round(d * 1000));
    };
    v.onerror = () => { URL.revokeObjectURL(url); reject(new Error('video load error')); };
    v.src = url;
  });
}

const MIME_EXT = {
  'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp', 'image/heic': 'heic',
  'video/mp4': 'mp4', 'video/quicktime': 'mov', 'video/webm': 'webm',
};

/**
 * Choose a filename extension for the blob being stored. Prefers the blob's
 * own MIME (which reflects any compression re-encode), then the original
 * file's name/type.
 */
function _extForBlob(blob, originalFile) {
  if (blob && blob.type && MIME_EXT[blob.type]) return MIME_EXT[blob.type];
  return _extFor(originalFile);
}

function _extFor(file) {
  const fromName = (file.name || '').split('.').pop();
  if (fromName && fromName.length <= 5 && /^[a-z0-9]+$/i.test(fromName)) return fromName.toLowerCase();
  return MIME_EXT[file.type || ''] || 'bin';
}

function _uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export default { isSupported, inspectMedia, saveMediaFile, getMediaBlob, getMediaObjectURL, deleteMediaFile };
